import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchBillingStatus, type BillingStatus } from '@/api/billing';
import { qk } from '@/api/queryKeys';

/**
 * The org's billing status, fetched once and shared.
 *
 * Before this hook, GET /api/billing/status went out 3× concurrently on every page
 * load — AuthContext's bootstrap, plus Sidebar and Topbar, which each call
 * `useNavItems()` and so each ran their own effect — and a 4th on the staff page.
 * Every copy was identical: the payload only changes when Stripe tells us it did.
 * Every consumer now reads one cache entry.
 *
 * Freshness is by invalidation, not polling — see `useInvalidateBillingStatus`.
 * Anything that can change a subscription (checkout return, add-on purchase, portal
 * return, plan change) must call it, or the UI will confidently show the old plan.
 */
export function useBillingStatus(options?: { enabled?: boolean; alwaysFresh?: boolean }) {
  return useQuery({
    queryKey: qk.billing.status,
    queryFn: fetchBillingStatus,
    enabled: options?.enabled ?? true,
    // Stripe state changes are announced (webhook → our DB), never guessed at from
    // the client. A long stale window is safe precisely because writes invalidate.
    staleTime: 5 * 60 * 1000,
    /**
     * `alwaysFresh` — re-verify on mount, for the one screen that shows the numbers.
     *
     * The invalidate-on-write rule holds for everything this payload carries EXCEPT
     * `aiCreditsUsed`: credits are spent all over the menu editor (every
     * `/api/ai/*` call in api/menus-ai.ts returns `creditsUsed`), and those writers have
     * no reasonable way to know a billing cache exists — the next AI feature would
     * forget again. Since BillingPage is the only reader of that field, it re-reads on
     * arrival rather than trusting a window in which credits may have been spent.
     * Every other consumer (nav, plan gates) keeps the shared cache.
     */
    refetchOnMount: options?.alwaysFresh ? 'always' : undefined,
  });
}

/**
 * Mark the billing status stale after something that could have changed it.
 *
 * Returns a stable callback so it can sit in a dependency array without re-firing
 * the effect that uses it.
 */
export function useInvalidateBillingStatus(): () => Promise<void> {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: qk.billing.status });
}

export type { BillingStatus };
